/**
 * Splits assistant text into paragraphs and list items for DOM rendering.
 *
 * Parsing stays in this module so the widget can build nodes with textContent
 * and never treat model output as HTML.
 */

export type MessageBlock =
    | { type: 'paragraph'; text: string }
    | { type: 'list'; items: string[] };

const LEADING_MARKER = /^(?:[•\-\*\u2013\u2014]|\d+[.)])\s+/;

export function parseMessageBlocks(content: string): MessageBlock[] {
    const lines = content.replace(/\r\n/g, '\n').split('\n');
    const blocks: MessageBlock[] = [];
    let paragraph: string[] = [];
    let items: string[] = [];

    const flushParagraph = (): void => {
        const text = paragraph.join(' ').trim();
        paragraph = [];
        if (text !== '') {
            blocks.push({ type: 'paragraph', text });
        }
    };

    const flushList = (): void => {
        if (items.length > 0) {
            blocks.push({ type: 'list', items });
            items = [];
        }
    };

    for (const raw of lines) {
        const line = raw.trim();
        if (line === '') {
            flushParagraph();
            flushList();
            continue;
        }

        const marked = LEADING_MARKER.exec(line);
        if (marked !== null) {
            flushParagraph();
            const item = line.slice(marked[0].length).trim();
            if (item !== '') {
                items.push(item);
            }
            continue;
        }

        flushList();
        paragraph.push(line);
    }

    flushParagraph();
    flushList();
    return blocks;
}

const SAFE_HREF =
    /https?:\/\/[^\s<>"')]+|\/[A-Za-z][A-Za-z0-9._~-]*(?:\/[A-Za-z0-9._~-]*)*(?:#[A-Za-z0-9._~-]*)?/g;

export type TextRun = { type: 'text'; value: string } | { type: 'link'; value: string };

/** Splits prose so site paths and http(s) URLs can be rendered as links. */
export function splitLinkRuns(text: string): TextRun[] {
    const runs: TextRun[] = [];
    const pattern = new RegExp(SAFE_HREF.source, 'g');
    let last = 0;
    let match = pattern.exec(text);
    while (match !== null) {
        if (match.index > last) {
            runs.push({ type: 'text', value: text.slice(last, match.index) });
        }
        runs.push({ type: 'link', value: match[0] });
        last = match.index + match[0].length;
        match = pattern.exec(text);
    }
    if (last < text.length) {
        runs.push({ type: 'text', value: text.slice(last) });
    }
    return runs.length > 0 ? runs : [{ type: 'text', value: text }];
}
